import React, { useState } from "react";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { PageHeader } from "../components/PageHeader";
import heroImage from "../assets/artem-sapegin-b18TRXc8UPQ-unsplash.jpg";
import { Tooltip } from "primereact/tooltip";

interface ContactFormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  services: string[];
  preferredDate: Date | null;
  preferredTime: string;
  message: string;
}

const serviceOptions = [
  "Web Development",
  "Custom Applications",
  "Database Design",
  "Cloud Solutions",
  "Security Audit",
  "Analytics",
  "Process Automation",
  "Data Visualization",
  "Technology Consulting",
];

const timeSlots = [
  { label: "9:00 AM", value: "09:00" },
  { label: "10:00 AM", value: "10:00" },
  { label: "11:00 AM", value: "11:00" },
  { label: "1:00 PM", value: "13:00" },
  { label: "2:00 PM", value: "14:00" },
  { label: "3:00 PM", value: "15:00" },
  { label: "4:00 PM", value: "16:00" },
  { label: "5:00 PM", value: "17:00" },
];

// Web3Forms (copied from li-website)
const WEB3FORMS_URL = "https://api.web3forms.com/submit";
const WEB3FORMS_KEY = "53856fbd-350c-4424-87b1-870e3cf6f977";

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    company: "",
    email: "",
    phone: "",
    services: [],
    preferredDate: null,
    preferredTime: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(
    null
  );

  const handleInputChange = (
    field: keyof ContactFormData,
    value: string | string[] | Date | null | undefined
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]:
        value ||
        (field === "services" ? [] : field === "preferredDate" ? null : ""),
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null); // Change from "idle" to null

    // safe serialization (no `any`)
    let preferred_date = "";
    const pd = (formData as { preferredDate?: unknown }).preferredDate;
    if (pd instanceof Date) preferred_date = pd.toISOString();
    else if (typeof pd === "string") {
      const d = new Date(pd);
      if (!isNaN(d.getTime())) preferred_date = d.toISOString();
    }

    const pt = (formData as { preferredTime?: unknown }).preferredTime;
    const preferred_time = typeof pt === "string" ? pt : "";

    const payload = {
      access_key: WEB3FORMS_KEY,
      name: formData.name?.trim() ?? "",
      email: formData.email?.trim() ?? "",
      company: formData.company?.trim() ?? "",
      phone: formData.phone?.trim() ?? "",
      message: formData.message?.trim() ?? "",
      services: Array.isArray(formData.services)
        ? formData.services.join(", ")
        : "",
      preferred_date,
      preferred_time,
      source: "logic-innova-website",
      page: typeof window !== "undefined" ? window.location.href : "",
    };

    try {
      const res = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (data?.success) {
        setSubmitStatus("success");
        // optionally reset formData here
      } else {
        setSubmitStatus("error");
      }
    } catch (err) {
      console.error("Contact submit error:", err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.name &&
      formData.email &&
      formData.services.length > 0 &&
      formData.preferredDate &&
      formData.preferredTime
    );
  };

  return (
    <>
      {/* Global tooltip for time zone hint */}
      <Tooltip target=".tz-help" showDelay={100} hideDelay={0} />

      {/* Top of Contact page anchor */}
      <div id="contact-top" style={{ scrollMarginTop: "96px" }} />

      {/* Hero Section */}
      <PageHeader
        title="Get in Touch"
        description="Ready to transform your business with innovative technology solutions? Let's start the conversation."
        backgroundImage={heroImage}
        height="500px"
        buttons={[
          {
            label: "Schedule Call",
            icon: "pi pi-calendar",
            onClick: () =>
              document
                .getElementById("contact-form")
                ?.scrollIntoView({ behavior: "smooth", block: "start" }),
          },
          {
            label: "Email Us",
            icon: "pi pi-envelope",
            severity: "secondary",
            outlined: true,
            onClick: () => {
              window.location.href = "mailto:info@logic-innova.com";
            },
          },
        ]}
      />

      {/* Contact form section anchor */}
      <div
        id="contact-form"
        style={{ scrollMarginTop: "96px" }}
        className="container mx-auto px-8 py-32 md:py-40 mb-8 md:mb-16"
      >
        <div className="text-center mb-20 pt-8 md:pt-12">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-gray-900 dark:text-white">
            Let's Get Started!
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-loose">
            Tell us about your project and we will get back to you within 24
            hours to schedule a consultation.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Card className="shadow-2xl border-0 bg-white dark:bg-gray-800 rounded-2xl">
            <div className="p-10 md:p-16">
              {submitStatus === "success" && (
                <Message
                  severity="success"
                  text="Thank you! We've received your message and will get back to you within 24 hours."
                  className="mb-6"
                />
              )}

              {submitStatus === "error" && (
                <Message
                  severity="error"
                  text="Something went wrong. Please try again or contact us directly."
                  className="mb-6"
                />
              )}

              <form onSubmit={handleSubmit} className="space-y-12 px-6 md:px-8">
                {/* Personal Information */}
                <div className="space-y-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 pt-8">
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ml-8 md:ml-12">
                    <div className="space-y-3">
                      <label className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                        Name *
                      </label>
                      <InputText
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        placeholder="Enter first and last name"
                        className="w-full p-3 text-base"
                        required
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                        Company
                      </label>
                      <InputText
                        value={formData.company}
                        onChange={(e) =>
                          handleInputChange("company", e.target.value)
                        }
                        placeholder="Your company name"
                        className="w-full p-3 text-base"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-8 mt-20">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 pt-8">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ml-8 md:ml-12">
                    <div className="space-y-3">
                      <label className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                        Email Address *
                      </label>
                      <InputText
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        placeholder="your.email@company.com"
                        className="w-full p-3 text-base"
                        required
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                        Phone Number
                      </label>
                      <InputText
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        placeholder="+1 (555) 123-4567"
                        className="w-full p-3 text-base"
                      />
                    </div>
                  </div>
                </div>

                {/* Services Selection */}
                <div className="space-y-8 mt-20">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 pt-8">
                    Services of Interest *
                  </h3>
                  <div className="space-y-6 ml-8 md:ml-12">
                    <p className="text-base text-gray-600 dark:text-gray-400">
                      Select all services you may be interested in (type to add
                      custom services)
                    </p>

                    <div className="w-full">
                      <Chips
                        value={formData.services}
                        onChange={(e) => handleInputChange("services", e.value)}
                        placeholder="Type and press Enter to add services, or select from the popular services below"
                        className="contact-chips w-full p-3 text-base text-gray-900 dark:text-white"
                        style={{ minWidth: "100%", width: "100%" }}
                        pt={{
                          container: {
                            className: "bg-transparent shadow-none",
                          },
                          token: {
                            className: "bg-transparent border-0 shadow-none",
                          },
                          input: { className: "bg-transparent" },
                        }}
                        required
                      />
                    </div>

                    <div className="space-y-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Popular Services:
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {serviceOptions.map((service) => (
                          <button
                            key={service}
                            type="button"
                            onClick={() => {
                              if (!formData.services.includes(service)) {
                                handleInputChange("services", [
                                  ...formData.services,
                                  service,
                                ]);
                              }
                            }}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border-2 ${
                              formData.services.includes(service)
                                ? "bg-blue-500 text-white border-blue-500 shadow-md"
                                : "bg-transparent dark:bg-transparent text-gray-700 dark:text-white border-gray-300 dark:border-gray-600 hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            }`}
                            style={{
                              color: formData.services.includes(service)
                                ? "white"
                                : undefined,
                            }}
                          >
                            {service}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scheduling */}
                <div className="space-y-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 pt-8">
                    Schedule Your Consultation
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ml-8 md:ml-12">
                    <div className="space-y-3">
                      <label className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                        Preferred Date *
                      </label>
                      <Calendar
                        value={formData.preferredDate}
                        onChange={(e) =>
                          handleInputChange("preferredDate", e.value)
                        }
                        placeholder="Select a date"
                        className="w-full p-2 text-base h-10"
                        minDate={new Date()}
                        showIcon
                        required
                      />
                    </div>

                    <div className="space-y-3">
                      <label className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                        Preferred Time *
                        <i
                          className="pi pi-info-circle tz-help ml-2 align-middle text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 cursor-help"
                          data-pr-tooltip="All times are in Central Standard Time (CST)."
                          data-pr-position="top"
                        />
                      </label>
                      <Dropdown
                        value={formData.preferredTime}
                        onChange={(e) =>
                          handleInputChange("preferredTime", e.value)
                        }
                        options={timeSlots}
                        placeholder="Select a time"
                        className="w-full p-2 text-base h-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 pt-8">
                    Project Details
                  </h3>
                  <div className="space-y-3">
                    <label className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                      Tell us about your goals and requirements
                    </label>
                    <InputTextarea
                      value={formData.message}
                      onChange={(e) =>
                        handleInputChange("message", e.target.value)
                      }
                      placeholder="Send us an overview of your project, goals, timeline, and any specific requirements..."
                      rows={6}
                      className="w-full p-4 pr-8 text-base resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    label={isSubmitting ? "Sending..." : "Send Message"}
                    icon="pi pi-send"
                    disabled={isSubmitting || !isFormValid()} // Add validation here
                  />
                </div>

                {/* Message space below the button */}
                <div className="pt-3 pb-6 min-h-[1.5rem]">
                  {submitStatus === "success" && (
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      Thanks! Your message was sent.
                    </p>
                  )}
                  {submitStatus === "error" && (
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      Failed to send the message. Please try again.
                    </p>
                  )}
                </div>
              </form>
            </div>
          </Card>
        </div>
      </div>

      <Divider className="my-48 md:my-64 opacity-0" />

      {/* Contact info cards (Email / Call) anchor */}
      <section
        id="contact-info"
        style={{ scrollMarginTop: "96px" }}
        className="max-w-5xl mx-auto"
      >
        <div className="flex flex-nowrap items-stretch gap-8">
          {/* Email Us */}
          <Card className="flex-1 flex flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
            <div className="p-8 flex flex-col flex-1">
              <div className="w-14 h-14 rounded-xl bg-blue-500/10 text-gray-900 dark:text-white flex items-center justify-center mb-6">
                {/* removed any text-white on the icon so it inherits */}
                <i className="pi pi-envelope text-xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Email Us
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Send us a detailed message and we'll respond within 24 hours.
              </p>
              <a
                href="mailto:info@logic-innova.com"
                className="mt-auto inline-flex items-center justify-center rounded-lg border border-blue-500 text-blue-600 dark:text-blue-300 hover:bg-blue-500/10 px-4 py-2 font-medium"
              >
                info@logic-innova.com
                <i className="pi pi-external-link ml-2 text-sm" />
              </a>
            </div>
          </Card>

          {/* Call Us */}
          <Card className="flex-1 flex flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
            <div className="p-8 flex flex-col flex-1">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 flex items-center justify-center mb-6">
                <i className="pi pi-phone text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Call Us
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Speak directly with our team during business hours.
              </p>
              <a
                href="tel:+18324478940"
                className="mt-auto inline-flex items-center justify-center rounded-lg border border-emerald-500 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-500/10 px-4 py-2 font-medium"
              >
                +1 (832) 447-8940
                <i className="pi pi-phone ml-2 text-sm"></i>
              </a>
            </div>
          </Card>
        </div>
      </section>

      <br />
      <br />

      {/* CTA Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 dark:from-blue-700 dark:via-blue-800 dark:to-indigo-900 py-32 md:py-40 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-white blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-white blur-2xl"></div>
        </div>

        <div className="container mx-auto px-8 md:px-12 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl md:text-2xl text-white mb-12 md:mb-16 max-w-3xl mx-auto leading-loose">
            Let's discuss how we can help transform your business with
            innovative technology solutions.
          </p>
        </div>
      </div>
    </>
  );
};
